import { useState, useEffect, useCallback } from 'react';
import { useChat } from '../context/ChatContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Switch } from './ui/switch';
import { 
  Megaphone,
  Plus,
  Play,
  Pause,
  Trash2,
  Edit,
  Copy,
  Send,
  Users,
  Target,
  Calendar,
  Clock,
  Zap,
  GitBranch,
  MessageSquare,
  Smartphone,
  Instagram,
  Facebook,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Download,
  Upload,
  Eye,
  EyeOff,
  Check,
  X,
  AlertCircle,
  Sparkles,
  Loader2,
  Settings,
  BarChart3,
  TrendingUp,
  ArrowRight,
  Mail,
  Phone,
  Globe,
  Filter,
  Layers,
  Save,
  FolderOpen,
  MoreVertical,
  Search,
  Hash,
  Workflow,
  CheckCircle2,
  Shield,
  RotateCcw,
  UserCheck,
  UserX
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { CampaignScheduleModal, ExecutionConfirmationModal, CampaignProgressTracker, DeliveryReportViewer } from './CampaignExecutionModals';

import { API_BASE_URL as API } from '../utils/apiConfig';

// Campaign type configurations
const CAMPAIGN_TYPES = [
  {
    id: 'cohort',
    name: 'Cohort Campaign',
    description: 'One-time send to selected audience',
    icon: Users,
    color: 'violet'
  },
  {
    id: 'journey',
    name: 'CLM Journey',
    description: 'Multi-step customer lifecycle journey',
    icon: GitBranch,
    color: 'blue'
  },
  {
    id: 'scheduled',
    name: 'Scheduled Campaign',
    description: 'Schedule for future date/time',
    icon: Calendar,
    color: 'amber'
  },
  {
    id: 'trigger',
    name: 'Trigger Campaign',
    description: 'Auto-send on specific events',
    icon: Zap,
    color: 'pink'
  },
  {
    id: 'retarget',
    name: 'Retarget Campaign',
    description: 'Target non-responders from past campaigns',
    icon: RotateCcw,
    color: 'emerald'
  }
];

// Channel configurations
const CHANNELS = [
  { id: 'sms', name: 'SMS', icon: MessageSquare, color: '#10B981' },
  { id: 'whatsapp', name: 'WhatsApp', icon: Phone, color: '#25D366' },
  { id: 'rcs', name: 'RCS', icon: Smartphone, color: '#4285F4' },
  { id: 'meta_ads', name: 'Meta Ads', icon: Instagram, color: '#E1306C' }
];

// Status badges
const STATUS_STYLES = {
  draft: { color: 'bg-gray-500', label: 'Draft' },
  scheduled: { color: 'bg-blue-500', label: 'Scheduled' },
  running: { color: 'bg-green-500', label: 'Running' },
  paused: { color: 'bg-amber-500', label: 'Paused' },
  completed: { color: 'bg-violet-500', label: 'Completed' },
  failed: { color: 'bg-red-500', label: 'Failed' }
};

// Segment options
const SEGMENTS = [
  { id: 'test', name: '🧪 Test Audience', description: '2 test numbers: 9811123206, 9910059861', isTest: true },
  { id: 'champions', name: 'Champions', description: 'High value, frequent, recent' },
  { id: 'loyal', name: 'Loyal Customers', description: 'Regular purchasers' },
  { id: 'at_risk', name: 'At Risk', description: '90-180 days inactive' },
  { id: 'one_timer_recent', name: 'One-Timers (Recent)', description: 'Single visit, last 90 days' },
  { id: 'high_value', name: 'High Value', description: 'LTV > ₹50,000' },
  { id: 'all', name: 'All Customers', description: 'Entire customer base' }
];

// Trigger types
const TRIGGER_TYPES = [
  { id: 'first_purchase', name: 'First Purchase', icon: '🛍️' },
  { id: 'birthday', name: 'Birthday', icon: '🎂' },
  { id: 'anniversary', name: 'Membership Anniversary', icon: '🎉' },
  { id: 'points_expiry', name: 'Points Expiry', icon: '⏰' },
  { id: 'tier_change', name: 'Tier Change', icon: '🏆' },
  { id: 'inactivity', name: 'Inactivity', icon: '😴' },
  { id: 'bill_amount', name: 'Bill Amount', icon: '💰' },
  { id: 'store_visit', name: 'Store Visit', icon: '🏪' },
  { id: 'points_balance', name: 'Points Balance', icon: '🎯' }
];

// Campaign Card Component
const CampaignCard = ({ campaign, onEdit, onExecute, onDelete, onSchedule, onViewProgress, onViewReport, onRetarget }) => {
  const typeConfig = CAMPAIGN_TYPES.find(t => t.id === campaign.type) || CAMPAIGN_TYPES[0];
  const TypeIcon = typeConfig.icon;
  const statusStyle = STATUS_STYLES[campaign.status] || STATUS_STYLES.draft;
  
  return (
    <Card className="hover:shadow-lg transition-all group cursor-pointer" onClick={() => onEdit(campaign)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${typeConfig.color}-500/20`}>
              <TypeIcon className={`w-5 h-5 text-${typeConfig.color}-500`} />
            </div>
            <div>
              <h3 className="font-semibold">{campaign.name}</h3>
              <p className="text-xs text-muted-foreground">{typeConfig.name}</p>
            </div>
          </div>
          <Badge className={`${statusStyle.color} text-white`}>{statusStyle.label}</Badge>
        </div>
        
        {/* Channels */}
        <div className="flex items-center gap-2 mb-3">
          {(campaign.channels || ['sms']).map(channel => {
            const ch = CHANNELS.find(c => c.id === channel);
            const ChannelIcon = ch?.icon || MessageSquare;
            return (
              <div 
                key={channel}
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${ch?.color}20` }}
              >
                <ChannelIcon className="w-3 h-3" style={{ color: ch?.color }} />
              </div>
            );
          })}
          {campaign.fallback_enabled && (
            <span className="text-xs text-muted-foreground ml-1">+ fallback</span>
          )}
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center p-2 rounded bg-muted/50">
            <p className="text-lg font-bold">
              {(campaign.confirmed_audience_size || campaign.analytics?.total_audience || campaign.estimated_audience_size || 0).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Audience</p>
          </div>
          <div className="text-center p-2 rounded bg-muted/50">
            <p className="text-lg font-bold text-green-500">{campaign.analytics?.delivered?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground">Delivered</p>
          </div>
          <div className="text-center p-2 rounded bg-muted/50">
            <p className="text-lg font-bold text-red-500">{campaign.analytics?.failed?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground">Failed</p>
          </div>
        </div>
        
        {/* Actions based on status */}
        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {/* Draft campaigns - Edit only */}
          {campaign.status === 'draft' && (
            <>
              <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={(e) => { e.stopPropagation(); onEdit(campaign); }}>
                <Edit className="w-3 h-3" /> Edit
              </Button>
            </>
          )}
          
          {/* Approved campaigns - Schedule & Execute */}
          {campaign.status === 'approved' && (
            <>
              <Button size="sm" className="flex-1 gap-1 bg-emerald-500 hover:bg-emerald-600" onClick={(e) => { e.stopPropagation(); onSchedule?.(campaign); }}>
                <Clock className="w-3 h-3" /> Schedule
              </Button>
              <Button size="sm" variant="destructive" className="flex-1 gap-1" onClick={(e) => { e.stopPropagation(); onExecute(campaign); }}>
                <Send className="w-3 h-3" /> Execute Now
              </Button>
            </>
          )}
          
          {/* Scheduled campaigns - Execute */}
          {campaign.status === 'scheduled' && (
            <>
              <Button size="sm" variant="destructive" className="flex-1 gap-1" onClick={(e) => { e.stopPropagation(); onExecute(campaign); }}>
                <Send className="w-3 h-3" /> Execute Now
              </Button>
            </>
          )}
          
          {/* Running campaigns - View Progress */}
          {campaign.status === 'running' && (
            <>
              <Button size="sm" className="flex-1 gap-1 bg-blue-500 hover:bg-blue-600" onClick={(e) => { e.stopPropagation(); onViewProgress?.(campaign); }}>
                <BarChart3 className="w-3 h-3" /> View Progress
              </Button>
            </>
          )}
          
          {/* Completed campaigns - View Report + Retarget */}
          {campaign.status === 'completed' && (
            <>
              <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={(e) => { e.stopPropagation(); onViewReport?.(campaign); }}>
                <BarChart3 className="w-3 h-3" /> Report
              </Button>
              {!campaign.is_retarget && (campaign.retarget_count || 0) < 3 && (
                <Button 
                  size="sm" 
                  className="flex-1 gap-1 bg-emerald-500 hover:bg-emerald-600" 
                  onClick={(e) => { e.stopPropagation(); onRetarget?.(campaign); }}
                >
                  <RotateCcw className="w-3 h-3" /> Retarget
                </Button>
              )}
            </>
          )}
          
          {/* Delete button for non-running campaigns */}
          {!['running', 'completed'].includes(campaign.status) && (
            <Button variant="ghost" size="icon" className="w-8 h-8" onClick={(e) => { e.stopPropagation(); onDelete(campaign); }}>
              <Trash2 className="w-3 h-3 text-destructive" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Journey Step Component
const JourneyStep = ({ step, index, onUpdate, onRemove }) => {
  const channel = CHANNELS.find(c => c.id === step.channel) || CHANNELS[0];
  const ChannelIcon = channel.icon;
  
  return (
    <div className="relative flex items-center gap-4 p-4 rounded-lg border bg-background group">
      {/* Timeline connector */}
      {index > 0 && (
        <div className="absolute -top-8 left-7 w-0.5 h-8 bg-border" />
      )}
      
      {/* Day badge */}
      <div className="flex flex-col items-center">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold">
          D+{step.day}
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <Select value={step.channel} onValueChange={(v) => onUpdate({ ...step, channel: v })}>
            <SelectTrigger className="w-40 h-8">
              <div className="flex items-center gap-2">
                <ChannelIcon className="w-4 h-4" style={{ color: channel.color }} />
                <span>{channel.name}</span>
              </div>
            </SelectTrigger>
            <SelectContent>
              {CHANNELS.filter(c => c.id !== 'meta_ads').map(c => (
                <SelectItem key={c.id} value={c.id}>
                  <div className="flex items-center gap-2">
                    <c.icon className="w-4 h-4" style={{ color: c.color }} />
                    {c.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Input 
            className="flex-1 h-8"
            placeholder="Day offset (e.g., 0, 3, 7)"
            type="number"
            value={step.day}
            onChange={(e) => onUpdate({ ...step, day: parseInt(e.target.value) || 0 })}
          />
        </div>
        
        <Textarea
          placeholder="Message content..."
          value={step.content || ''}
          onChange={(e) => onUpdate({ ...step, content: e.target.value })}
          className="min-h-[60px] text-sm"
        />
      </div>
      
      {/* Remove button */}
      <Button 
        variant="ghost" 
        size="icon"
        className="opacity-0 group-hover:opacity-100"
        onClick={() => onRemove(index)}
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
};

// Main Campaign Builder Component
export default function CampaignBuilder() {
  const { selectedBrand } = useChat();
  
  // Campaign list state
  const [campaigns, setCampaigns] = useState([]);
  const [pendingApprovalCampaigns, setPendingApprovalCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Builder state
  const [isCreating, setIsCreating] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [currentStep, setCurrentStep] = useState(0); // Wizard step
  const [approvalView, setApprovalView] = useState(null); // Campaign in approval workflow view
  
  // Execution modal states
  const [scheduleModalCampaign, setScheduleModalCampaign] = useState(null);
  const [executeModalCampaign, setExecuteModalCampaign] = useState(null);
  const [progressTrackerCampaign, setProgressTrackerCampaign] = useState(null);
  const [deliveryReportCampaign, setDeliveryReportCampaign] = useState(null);
  
  // Retargeting state
  const [retargetOpportunities, setRetargetOpportunities] = useState([]);
  const [showRetargetModal, setShowRetargetModal] = useState(null);
  const [retargetingEligible, setRetargetingEligible] = useState(null);
  const [retargetLoading, setRetargetLoading] = useState(false);
  const [creatingRetarget, setCreatingRetarget] = useState(false);
  
  // Form state
  const [campaignType, setCampaignType] = useState('cohort');
  const [campaignName, setCampaignName] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [selectedSegment, setSelectedSegment] = useState('');
  const [selectedChannels, setSelectedChannels] = useState(['sms']);
  const [fallbackEnabled, setFallbackEnabled] = useState(true);
  const [smsContent, setSmsContent] = useState('');
  const [whatsappContent, setWhatsappContent] = useState('');
  const [journeySteps, setJourneySteps] = useState([{ day: 0, channel: 'sms', content: '' }]);
  const [triggerType, setTriggerType] = useState('');
  const [triggerConditions, setTriggerConditions] = useState([]);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  
  // Preview state
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isLiveData, setIsLiveData] = useState(false);
  
  // AI generation state
  const [generating, setGenerating] = useState(false);
  
  // Execution state
  const [executing, setExecuting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Check for live data
  const getDbBrandId = useCallback(() => {
    return selectedBrand?.db_brand_id || selectedBrand?.settings?.db_brand_id || null;
  }, [selectedBrand]);
  
  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    if (!selectedBrand) return;
    
    setLoading(true);
    try {
      // Fetch regular campaigns
      const response = await axios.get(
        `${API}/api/campaigns?brand_id=${selectedBrand.brand_id}`,
        { withCredentials: true }
      );
      setCampaigns(response.data.data?.campaigns || []);
      
      // Also fetch campaigns pending approval (from AI recommendations)
      try {
        const pendingResponse = await axios.get(
          `${API}/api/campaigns/pending-approval?brand_id=${selectedBrand.brand_id}`,
          { withCredentials: true }
        );
        setPendingApprovalCampaigns(pendingResponse.data.campaigns || []);
      } catch (pendingErr) {
        console.error('Fetch pending approvals error:', pendingErr);
      }
      
      // Check if using live data
      const dbBrandId = getDbBrandId();
      setIsLiveData(!!dbBrandId);
    } catch (err) {
      console.error('Fetch campaigns error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, getDbBrandId]);
  
  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);
  
  // Fetch retargeting opportunities
  const fetchRetargetOpportunities = useCallback(async () => {
    if (!selectedBrand) return;
    
    try {
      const response = await axios.get(
        `${API}/api/campaigns/retarget-opportunities?brand_id=${selectedBrand.brand_id}`,
        { withCredentials: true }
      );
      setRetargetOpportunities(response.data.opportunities || []);
    } catch (err) {
      console.error('Fetch retarget opportunities error:', err);
    }
  }, [selectedBrand]);
  
  useEffect(() => {
    if (activeTab === 'retarget') {
      fetchRetargetOpportunities();
    }
  }, [activeTab, fetchRetargetOpportunities]);
  
  // Check retarget eligibility for a campaign
  const checkRetargetEligibility = async (campaign) => {
    setRetargetLoading(true);
    setShowRetargetModal(campaign);
    
    try {
      const response = await axios.get(
        `${API}/api/campaigns/${campaign.campaign_id}/retarget-eligible?exclusion_days=7`,
        { withCredentials: true }
      );
      setRetargetingEligible(response.data);
    } catch (err) {
      console.error('Check retarget eligibility error:', err);
      toast.error(err.response?.data?.detail || 'Failed to check retarget eligibility');
      setShowRetargetModal(null);
    } finally {
      setRetargetLoading(false);
    }
  };
  
  // Create retarget campaign
  const createRetargetCampaign = async (parentCampaign, exclusionDays = 7, customMessage = null) => {
    setCreatingRetarget(true);
    
    try {
      const response = await axios.post(
        `${API}/api/campaigns/${parentCampaign.campaign_id}/create-retarget`,
        { 
          exclusion_days: exclusionDays,
          custom_message: customMessage
        },
        { withCredentials: true }
      );
      
      toast.success(response.data.message || 'Retargeting campaign created!');
      setShowRetargetModal(null);
      setRetargetingEligible(null);
      fetchCampaigns();
      fetchRetargetOpportunities();
      
      // Open the approval workflow for the new campaign
      if (response.data.campaign) {
        setApprovalView(response.data.campaign);
      }
    } catch (err) {
      console.error('Create retarget campaign error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create retargeting campaign');
    } finally {
      setCreatingRetarget(false);
    }
  };
  
  // Preview audience - with live data support
  const previewAudience = async () => {
    if (!selectedBrand || !selectedSegment) return;
    
    setPreviewLoading(true);
    
    // Try live data first
    const dbBrandId = getDbBrandId();
    if (dbBrandId) {
      try {
        const response = await axios.get(
          `${API}/api/data-bridge/customers?brand_id=${dbBrandId}&limit=20`,
          { withCredentials: true }
        );
        
        if (response.data.success && response.data.data?.customers) {
          setAudiencePreview({
            total: response.data.data.total || response.data.data.customers.length,
            sample: response.data.data.customers.map(c => ({
              mobile: c.mobileno,
              name: c.cust_name || 'Customer',
              city: c.city_name,
              tier: c.tier_name
            })),
            source: 'live'
          });
          setPreviewLoading(false);
          return;
        }
      } catch (err) {
        console.error('Live audience preview error:', err);
      }
    }
    
    // Fallback to original
    try {
      const response = await axios.post(
        `${API}/api/campaigns/preview/audience`,
        {
          brand_id: selectedBrand.brand_id,
          type: 'segment',
          segment_id: selectedSegment
        },
        { withCredentials: true }
      );
      setAudiencePreview(response.data.data);
    } catch (err) {
      console.error('Audience preview error:', err);
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Generate AI content
  const generateContent = async () => {
    if (!selectedBrand) return;
    
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/api/campaigns/content/generate`,
        {
          brand_name: selectedBrand.brand_name || 'Brand',
          campaign_type: campaignType,
          target_segment: selectedSegment,
          language: 'hinglish',
          channel: selectedChannels[0]
        },
        { withCredentials: true }
      );
      
      if (response.data.data?.content) {
        setSmsContent(response.data.data.content);
      }
    } catch (err) {
      console.error('Generate content error:', err);
    } finally {
      setGenerating(false);
    }
  };
  
  // Save campaign
  const saveCampaign = async (execute = false) => {
    if (!selectedBrand) {
      toast.error('No brand selected');
      setError('No brand selected');
      return;
    }
    
    if (!campaignName.trim()) {
      toast.error('Please enter a campaign name');
      setError('Campaign name is required');
      return;
    }
    
    if (!selectedSegment) {
      toast.error('Please select an audience segment');
      setError('Audience segment is required');
      return;
    }
    
    if (!smsContent.trim() && selectedChannels.includes('sms')) {
      toast.error('Please enter SMS content');
      setError('SMS content is required');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    const campaignData = {
      brand_id: selectedBrand.brand_id,
      name: campaignName,
      description: campaignDescription,
      type: campaignType,
      audience_type: 'segment',
      segment_id: selectedSegment,
      channels: selectedChannels,
      fallback_enabled: fallbackEnabled,
      sms_content: smsContent,
      whatsapp_content: { template: whatsappContent },
      journey_config: campaignType === 'journey' ? { steps: journeySteps } : {},
      trigger_config: campaignType === 'trigger' ? { 
        trigger_type: triggerType,
        conditions: triggerConditions
      } : {},
      schedule_config: campaignType === 'scheduled' ? {
        scheduled_at: scheduleDate && scheduleTime ? `${scheduleDate}T${scheduleTime}:00` : null,
        timezone: 'Asia/Kolkata'
      } : {}
    };
    
    console.log('Saving campaign:', campaignData);
    
    try {
      let response;
      if (editingCampaign) {
        response = await axios.put(
          `${API}/api/campaigns/${editingCampaign._id}`,
          campaignData,
          { withCredentials: true }
        );
        toast.success('Campaign updated successfully!');
      } else {
        response = await axios.post(
          `${API}/api/campaigns`,
          campaignData,
          { withCredentials: true }
        );
        toast.success('Campaign created successfully!');
      }
      
      console.log('Campaign saved:', response.data);
      
      const savedCampaign = response.data.data;
      
      // Execute if requested
      if (execute && savedCampaign?._id) {
        console.log('Executing campaign:', savedCampaign._id);
        await executeCampaign(savedCampaign);
      } else {
        resetForm();
        fetchCampaigns();
      }
    } catch (err) {
      console.error('Save campaign error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to save campaign';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };
  
  // Execute campaign
  const executeCampaign = async (campaign) => {
    if (!selectedBrand) {
      toast.error('No brand selected');
      return;
    }
    
    setExecuting(true);
    toast.loading('Executing campaign...', { id: 'campaign-execute' });
    
    try {
      const response = await axios.post(
        `${API}/api/campaigns/${campaign._id}/execute`,
        { brand_id: selectedBrand.brand_id },
        { withCredentials: true }
      );
      
      const result = response.data.data;
      toast.dismiss('campaign-execute');
      
      if (result.sent > 0) {
        toast.success(
          `Campaign executed! ${result.sent} messages sent via ${Object.keys(result.by_channel || {}).join(', ')}`,
          { duration: 5000 }
        );
      } else if (result.failed > 0) {
        toast.warning(`Campaign completed with ${result.failed} failures`, { duration: 5000 });
      } else {
        toast.success('Campaign executed successfully!');
      }
      
      resetForm();
      fetchCampaigns();
    } catch (err) {
      console.error('Execute campaign error:', err);
      toast.dismiss('campaign-execute');
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to execute campaign';
      toast.error(errorMessage);
    } finally {
      setExecuting(false);
    }
  };
  
  // Delete campaign
  const deleteCampaign = async (campaign) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await axios.delete(
        `${API}/api/campaigns/${campaign._id}?brand_id=${selectedBrand.brand_id}`,
        { withCredentials: true }
      );
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (err) {
      console.error('Delete campaign error:', err);
      toast.error('Failed to delete campaign');
    }
  };
  
  // Edit campaign
  const editCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignType(campaign.type);
    setCampaignName(campaign.name);
    setCampaignDescription(campaign.description || '');
    setSelectedSegment(campaign.audience?.segment_id || '');
    setSelectedChannels(campaign.channels || ['sms']);
    setFallbackEnabled(campaign.fallback_enabled !== false);
    setSmsContent(campaign.content?.sms || '');
    setWhatsappContent(campaign.content?.whatsapp?.template || '');
    setJourneySteps(campaign.journey_config?.steps || [{ day: 0, channel: 'sms', content: '' }]);
    setTriggerType(campaign.trigger_config?.trigger_type || '');
    setTriggerConditions(campaign.trigger_config?.conditions || []);
    setIsCreating(true);
    setCurrentStep(0);
  };
  
  // Reset form
  const resetForm = () => {
    setIsCreating(false);
    setEditingCampaign(null);
    setCurrentStep(0);
    setCampaignType('cohort');
    setCampaignName('');
    setCampaignDescription('');
    setSelectedSegment('');
    setSelectedChannels(['sms']);
    setFallbackEnabled(true);
    setSmsContent('');
    setWhatsappContent('');
    setJourneySteps([{ day: 0, channel: 'sms', content: '' }]);
    setTriggerType('');
    setTriggerConditions([]);
    setScheduleDate('');
    setScheduleTime('');
    setAudiencePreview(null);
  };
  
  // Add journey step
  const addJourneyStep = () => {
    const lastDay = journeySteps.length > 0 ? journeySteps[journeySteps.length - 1].day : -1;
    setJourneySteps([...journeySteps, { day: lastDay + 3, channel: 'sms', content: '' }]);
  };
  
  // Toggle channel selection
  const toggleChannel = (channelId) => {
    if (selectedChannels.includes(channelId)) {
      if (selectedChannels.length > 1) {
        setSelectedChannels(selectedChannels.filter(c => c !== channelId));
      }
    } else {
      setSelectedChannels([...selectedChannels, channelId]);
    }
  };
  
  // Filter campaigns by type
  const filteredCampaigns = activeTab === 'all' 
    ? [...pendingApprovalCampaigns, ...campaigns]
    : [...pendingApprovalCampaigns, ...campaigns].filter(c => c.type === activeTab);

  // Handle opening approval workflow
  const openApprovalWorkflow = (campaign) => {
    setApprovalView(campaign);
  };

  const closeApprovalWorkflow = () => {
    setApprovalView(null);
    fetchCampaigns(); // Refresh the list
  };

  // If viewing approval workflow
  if (approvalView) {
    const CampaignApprovalWorkflow = require('./CampaignApprovalWorkflow').default;
    return (
      <CampaignApprovalWorkflow 
        campaign={approvalView}
        onClose={closeApprovalWorkflow}
        onComplete={() => {
          closeApprovalWorkflow();
          toast.success('Campaign approval completed!');
        }}
      />
    );
  }

  // Campaign List View
  if (!isCreating) {
    return (
      <div className="space-y-6" data-testid="campaign-builder">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-pink-500 via-rose-500 to-orange-500 shadow-lg shadow-pink-500/30">
              <Megaphone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Campaign Builder</h1>
              <p className="text-muted-foreground">Create and manage multi-channel campaigns</p>
            </div>
          </div>
          
          <Button 
            onClick={() => setIsCreating(true)}
            className="gap-2 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700"
          >
            <Plus className="w-4 h-4" />
            Create Campaign
          </Button>
        </div>

        {/* Pending Approvals Alert */}
        {pendingApprovalCampaigns.length > 0 && (
          <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/5 border-amber-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/20">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">AI-Recommended Campaigns Awaiting Approval</h3>
                    <p className="text-sm text-muted-foreground">
                      {pendingApprovalCampaigns.length} campaign{pendingApprovalCampaigns.length > 1 ? 's' : ''} need your review
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="border-amber-500 text-amber-500">
                  {pendingApprovalCampaigns.length} Pending
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Campaign Type Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-6 w-full max-w-3xl">
            <TabsTrigger value="all" className="gap-2">
              <Layers className="w-4 h-4" />
              All
            </TabsTrigger>
            {CAMPAIGN_TYPES.map(type => {
              const Icon = type.icon;
              return (
                <TabsTrigger key={type.id} value={type.id} className="gap-2">
                  <Icon className="w-4 h-4" />
                  {type.id === 'retarget' ? 'Retarget' : type.name.split(' ')[0]}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        {/* AI-Recommended Campaigns (Pending Approval) */}
        {pendingApprovalCampaigns.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              AI-Recommended Campaigns (Review Required)
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingApprovalCampaigns.map(campaign => (
                <Card 
                  key={campaign.campaign_id}
                  className="cursor-pointer hover:shadow-lg transition-all border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-orange-500/5"
                  onClick={() => openApprovalWorkflow(campaign)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-amber-500/20">
                          <Sparkles className="w-4 h-4 text-amber-500" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm">{campaign.name}</h4>
                          <p className="text-xs text-muted-foreground">
                            {campaign.insight_source?.replace(/_/g, ' ') || 'AI Recommendation'}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-amber-500 text-white text-xs">
                        {campaign.status === 'draft' ? 'Review' : 'Pending'}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                      <div className="p-2 rounded bg-muted/50 text-center">
                        <p className="font-bold">{(campaign.estimated_audience_size || campaign.segment?.estimated_size || 0).toLocaleString()}</p>
                        <p className="text-muted-foreground">Audience</p>
                      </div>
                      <div className="p-2 rounded bg-muted/50 text-center">
                        <p className="font-bold text-emerald-500">{campaign.expected_roi?.roi_multiplier || '4-6x'}</p>
                        <p className="text-muted-foreground">ROI</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-amber-500 rounded-full"
                          style={{ 
                            width: `${((campaign.approval_history?.filter(a => a.action === 'approve')?.length || 0) / 3) * 100}%` 
                          }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {campaign.approval_history?.filter(a => a.action === 'approve')?.length || 0}/3 steps
                      </span>
                    </div>
                    
                    <Button 
                      size="sm" 
                      className="w-full mt-3 gap-2 bg-amber-500 hover:bg-amber-600"
                      onClick={(e) => { e.stopPropagation(); openApprovalWorkflow(campaign); }}
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      Start Review
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        
        {/* Retarget Opportunities Section - Show when on retarget tab */}
        {activeTab === 'retarget' && (
          <div className="space-y-4">
            <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/5 border-emerald-500/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-500/20">
                      <RotateCcw className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Retarget Non-Responders</h3>
                      <p className="text-sm text-muted-foreground">
                        Target customers who received a campaign but didn't shop within 7 days
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="border-emerald-500 text-emerald-500">
                    {retargetOpportunities.filter(o => o.retarget_potential?.can_retarget).length} Opportunities
                  </Badge>
                </div>
              </CardContent>
            </Card>
            
            {retargetOpportunities.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <UserCheck className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-semibold mb-2">No Retarget Opportunities Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Execute some campaigns first - non-responders can be retargeted after 7 days
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {retargetOpportunities.map(opportunity => (
                  <Card 
                    key={opportunity.campaign_id}
                    className={`cursor-pointer hover:shadow-lg transition-all ${
                      opportunity.retarget_potential?.can_retarget 
                        ? 'border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-teal-500/5'
                        : 'opacity-60'
                    }`}
                    onClick={() => opportunity.retarget_potential?.can_retarget && checkRetargetEligibility(opportunity)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`p-2 rounded-lg ${opportunity.is_retarget ? 'bg-blue-500/20' : 'bg-violet-500/20'}`}>
                            {opportunity.is_retarget ? (
                              <RotateCcw className="w-4 h-4 text-blue-500" />
                            ) : (
                              <Users className="w-4 h-4 text-violet-500" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-semibold text-sm line-clamp-1">{opportunity.campaign_name}</h4>
                            <p className="text-xs text-muted-foreground">
                              {opportunity.is_retarget ? 'Retarget Campaign' : opportunity.campaign_type}
                            </p>
                          </div>
                        </div>
                        {opportunity.retarget_potential?.can_retarget ? (
                          <Badge className="bg-emerald-500 text-white text-xs">Eligible</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            {opportunity.retarget_potential?.retarget_count >= 3 ? 'Max Retries' : 'Too Small'}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                        <div className="p-2 rounded bg-muted/50 text-center">
                          <p className="font-bold">{(opportunity.metrics?.total_audience || 0).toLocaleString()}</p>
                          <p className="text-muted-foreground">Sent</p>
                        </div>
                        <div className="p-2 rounded bg-muted/50 text-center">
                          <p className="font-bold text-green-500">{(opportunity.metrics?.delivered || 0).toLocaleString()}</p>
                          <p className="text-muted-foreground">Delivered</p>
                        </div>
                        <div className="p-2 rounded bg-muted/50 text-center">
                          <p className="font-bold text-amber-500">{opportunity.metrics?.response_rate?.toFixed(1) || 0}%</p>
                          <p className="text-muted-foreground">Response</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <UserX className="w-3 h-3" />
                          <span>~{(opportunity.retarget_potential?.eligible_estimate || 0).toLocaleString()} non-responders</span>
                        </div>
                        <span className="text-muted-foreground">
                          RT #{opportunity.retarget_potential?.retarget_count || 0}/3
                        </span>
                      </div>
                      
                      {opportunity.retarget_potential?.can_retarget && (
                        <Button 
                          size="sm" 
                          className="w-full mt-3 gap-2 bg-emerald-500 hover:bg-emerald-600"
                          onClick={(e) => { e.stopPropagation(); checkRetargetEligibility(opportunity); }}
                        >
                          <RotateCcw className="w-3 h-3" />
                          Create Retarget Campaign
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Campaign Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredCampaigns.length === 0 && pendingApprovalCampaigns.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Megaphone className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-semibold mb-2">No campaigns yet</h3>
              <p className="text-muted-foreground mb-4">Create your first campaign to engage customers</p>
              <Button onClick={() => setIsCreating(true)} className="gap-2">
                <Plus className="w-4 h-4" />
                Create Campaign
              </Button>
            </CardContent>
          </Card>
        ) : campaigns.length > 0 && (
          <>
            <h3 className="font-semibold flex items-center gap-2 mt-6">
              <Megaphone className="w-4 h-4" />
              All Campaigns
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaigns.map(campaign => (
                <CampaignCard
                  key={`all-${campaign._id || campaign.campaign_id}`}
                  campaign={campaign}
                  onEdit={editCampaign}
                  onExecute={(c) => setExecuteModalCampaign(c)}
                  onDelete={deleteCampaign}
                  onSchedule={(c) => setScheduleModalCampaign(c)}
                  onViewProgress={(c) => setProgressTrackerCampaign(c)}
                  onViewReport={(c) => setDeliveryReportCampaign(c)}
                  onRetarget={(c) => checkRetargetEligibility(c)}
                />
              ))}
            </div>
          </>
        )}
        
        {/* Modals for Execution Flow */}
        {scheduleModalCampaign && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
              <CardContent className="p-6">
                <CampaignScheduleModal
                  campaign={scheduleModalCampaign}
                  onClose={() => setScheduleModalCampaign(null)}
                  onScheduled={(data) => {
                    setScheduleModalCampaign(null);
                    setExecuteModalCampaign(scheduleModalCampaign);
                    fetchCampaigns();
                  }}
                />
              </CardContent>
            </Card>
          </div>
        )}
        
        {executeModalCampaign && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
              <CardContent className="p-6">
                <ExecutionConfirmationModal
                  campaign={executeModalCampaign}
                  onClose={() => setExecuteModalCampaign(null)}
                  onExecuted={(data) => {
                    setExecuteModalCampaign(null);
                    if (data.status === 'running') {
                      setProgressTrackerCampaign({ ...executeModalCampaign, campaign_id: executeModalCampaign.campaign_id });
                    }
                    fetchCampaigns();
                  }}
                />
              </CardContent>
            </Card>
          </div>
        )}
        
        {progressTrackerCampaign && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
              <CardContent className="p-6">
                <CampaignProgressTracker
                  campaignId={progressTrackerCampaign.campaign_id}
                  onClose={() => {
                    setProgressTrackerCampaign(null);
                    fetchCampaigns();
                  }}
                />
              </CardContent>
            </Card>
          </div>
        )}
        
        {deliveryReportCampaign && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
              <CardContent className="p-6">
                <DeliveryReportViewer
                  campaignId={deliveryReportCampaign.campaign_id}
                  onClose={() => setDeliveryReportCampaign(null)}
                />
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Retarget Campaign Modal */}
        {showRetargetModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="retarget-modal">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
              <CardHeader className="pb-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-500/20">
                      <RotateCcw className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Create Retarget Campaign</CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Target non-responders from "{showRetargetModal.campaign_name}"
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => { setShowRetargetModal(null); setRetargetingEligible(null); }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {retargetLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mb-4" />
                    <p className="text-muted-foreground">Analyzing campaign data...</p>
                  </div>
                ) : retargetingEligible ? (
                  <>
                    {/* Parent Campaign Stats */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm">Original Campaign Performance</h4>
                      <div className="grid grid-cols-4 gap-3">
                        <div className="p-3 rounded-lg bg-muted/50 text-center">
                          <p className="text-lg font-bold">{(retargetingEligible.original_audience || 0).toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">Total Sent</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/50 text-center">
                          <p className="text-lg font-bold text-green-500">{(retargetingEligible.delivered || 0).toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">Delivered</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/50 text-center">
                          <p className="text-lg font-bold text-blue-500">{(retargetingEligible.responded || 0).toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">Responded</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/50 text-center">
                          <p className="text-lg font-bold text-red-500">{(retargetingEligible.failed || 0).toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">Failed</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Retarget Audience */}
                    <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/5 border-emerald-500/30">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-emerald-500/20">
                              <UserX className="w-5 h-5 text-emerald-500" />
                            </div>
                            <div>
                              <h4 className="font-semibold">Non-Responders (Eligible for Retarget)</h4>
                              <p className="text-sm text-muted-foreground">
                                Customers who received the message but didn't shop within {retargetingEligible.exclusion_days || 7} days
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-emerald-500">
                              {(retargetingEligible.eligible_for_retarget || 0).toLocaleString()}
                            </p>
                            <p className="text-xs text-muted-foreground">customers</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-3 text-xs">
                          <div className="p-2 rounded bg-white/50 dark:bg-black/20 text-center">
                            <p className="font-bold">{(retargetingEligible.analytics?.delivery_rate || 0).toFixed(1)}%</p>
                            <p className="text-muted-foreground">Delivery Rate</p>
                          </div>
                          <div className="p-2 rounded bg-white/50 dark:bg-black/20 text-center">
                            <p className="font-bold">{(retargetingEligible.analytics?.response_rate || 0).toFixed(1)}%</p>
                            <p className="text-muted-foreground">Response Rate</p>
                          </div>
                          <div className="p-2 rounded bg-white/50 dark:bg-black/20 text-center">
                            <p className="font-bold">{(retargetingEligible.analytics?.retarget_rate || 0).toFixed(1)}%</p>
                            <p className="text-muted-foreground">Retarget Rate</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Info Box */}
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-blue-600 dark:text-blue-400 mb-1">What happens next?</p>
                        <ul className="text-muted-foreground space-y-1">
                          <li>• A new retarget campaign will be created in Draft status</li>
                          <li>• AI will generate fresh message variants for retargeting</li>
                          <li>• You'll go through the 3-step approval workflow before sending</li>
                        </ul>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t">
                      <Button 
                        variant="outline" 
                        onClick={() => { setShowRetargetModal(null); setRetargetingEligible(null); }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        className="gap-2 bg-emerald-500 hover:bg-emerald-600"
                        onClick={() => createRetargetCampaign(showRetargetModal)}
                        disabled={creatingRetarget || (retargetingEligible.eligible_for_retarget || 0) < 10}
                        data-testid="create-retarget-btn"
                      >
                        {creatingRetarget ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <RotateCcw className="w-4 h-4" />
                            Create Retarget Campaign
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <AlertCircle className="w-8 h-8 text-amber-500 mb-4" />
                    <p className="text-muted-foreground">Failed to load eligibility data</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Send className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{campaigns.length}</p>
                <p className="text-sm text-muted-foreground">Total Campaigns</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Check className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {campaigns.reduce((sum, c) => sum + (c.analytics?.delivered || 0), 0).toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Messages Delivered</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-violet-500/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-violet-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {campaigns.reduce((sum, c) => sum + (c.analytics?.total_audience || 0), 0).toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Total Reach</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {campaigns.length > 0 
                    ? Math.round(campaigns.reduce((sum, c) => {
                        const total = c.analytics?.total_audience || 0;
                        const delivered = c.analytics?.delivered || 0;
                        return sum + (total > 0 ? delivered / total : 0);
                      }, 0) / campaigns.length * 100)
                    : 0}%
                </p>
                <p className="text-sm text-muted-foreground">Avg. Delivery Rate</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Campaign Builder Wizard View
  return (
    <div className="space-y-6" data-testid="campaign-wizard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={resetForm}>
            <ChevronRight className="w-4 h-4 rotate-180 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-bold">
              {editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}
            </h1>
            <p className="text-sm text-muted-foreground">
              Step {currentStep + 1} of 4
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => saveCampaign(false)}>
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </Button>
        </div>
      </div>
      
      {/* Progress */}
      <div className="flex items-center gap-4">
        {['Type', 'Audience', 'Content', 'Review'].map((step, idx) => (
          <div 
            key={step}
            className={`flex items-center gap-2 ${idx <= currentStep ? 'text-primary' : 'text-muted-foreground'}`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              idx < currentStep ? 'bg-primary text-primary-foreground' :
              idx === currentStep ? 'bg-primary/20 text-primary border-2 border-primary' :
              'bg-muted'
            }`}>
              {idx < currentStep ? <Check className="w-4 h-4" /> : idx + 1}
            </div>
            <span className="text-sm font-medium hidden md:block">{step}</span>
            {idx < 3 && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="p-6">
          {/* Step 1: Campaign Type */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-4">Choose Campaign Type</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {CAMPAIGN_TYPES.map(type => {
                    const Icon = type.icon;
                    const isSelected = campaignType === type.id;
                    return (
                      <Card 
                        key={type.id}
                        className={`cursor-pointer transition-all ${
                          isSelected ? `border-${type.color}-500 bg-${type.color}-500/5` : 'hover:border-primary/50'
                        }`}
                        onClick={() => setCampaignType(type.id)}
                      >
                        <CardContent className="p-4 flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-${type.color}-500/20`}>
                            <Icon className={`w-6 h-6 text-${type.color}-500`} />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium">{type.name}</h3>
                            <p className="text-sm text-muted-foreground">{type.description}</p>
                          </div>
                          {isSelected && <Check className="w-5 h-5 text-primary" />}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
              
              <div className="space-y-4">
                <Input
                  placeholder="Campaign Name"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  className="text-lg"
                />
                <Textarea
                  placeholder="Description (optional)"
                  value={campaignDescription}
                  onChange={(e) => setCampaignDescription(e.target.value)}
                />
              </div>
              
              {/* Trigger Type (if trigger campaign) */}
              {campaignType === 'trigger' && (
                <div>
                  <h3 className="font-medium mb-3">Select Trigger Event</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {TRIGGER_TYPES.map(trigger => (
                      <Button
                        key={trigger.id}
                        variant={triggerType === trigger.id ? 'default' : 'outline'}
                        className="justify-start gap-2 h-auto py-3"
                        onClick={() => setTriggerType(trigger.id)}
                      >
                        <span>{trigger.icon}</span>
                        <span className="text-sm">{trigger.name}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Schedule (if scheduled campaign) */}
              {campaignType === 'scheduled' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Schedule Date</label>
                    <Input
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Schedule Time</label>
                    <Input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Step 2: Audience */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-4">Select Target Audience</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {SEGMENTS.map(segment => (
                    <Card 
                      key={segment.id}
                      className={`cursor-pointer transition-all ${
                        selectedSegment === segment.id 
                          ? 'border-primary bg-primary/5' 
                          : segment.isTest 
                            ? 'border-amber-500/50 bg-amber-500/5 hover:border-amber-500' 
                            : 'hover:border-primary/50'
                      }`}
                      onClick={() => { setSelectedSegment(segment.id); setAudiencePreview(null); }}
                    >
                      <CardContent className="p-4 flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{segment.name}</h3>
                            {segment.isTest && (
                              <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
                                Recommended for Testing
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{segment.description}</p>
                        </div>
                        {selectedSegment === segment.id && <Check className="w-5 h-5 text-primary" />}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
              
              {/* Audience Preview */}
              {selectedSegment && (
                <div>
                  <Button 
                    variant="outline" 
                    onClick={previewAudience}
                    disabled={previewLoading}
                    className="gap-2"
                  >
                    {previewLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                    Preview Audience
                  </Button>
                  
                  {audiencePreview && (
                    <Card className="mt-4">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-medium">Audience Preview</h3>
                          <Badge variant="secondary">
                            {audiencePreview.total?.toLocaleString()} customers
                          </Badge>
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {audiencePreview.sample?.map((customer, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm p-2 rounded bg-muted/50">
                              <span>{customer.name || customer.mobile}</span>
                              <span className="text-muted-foreground">{customer.mobile}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Step 3: Content & Channels */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* Channel Selection */}
              <div>
                <h2 className="text-lg font-semibold mb-4">Select Channels</h2>
                <div className="flex flex-wrap gap-3">
                  {CHANNELS.map(channel => {
                    const Icon = channel.icon;
                    const isSelected = selectedChannels.includes(channel.id);
                    return (
                      <Button
                        key={channel.id}
                        variant={isSelected ? 'default' : 'outline'}
                        className="gap-2"
                        onClick={() => toggleChannel(channel.id)}
                        style={isSelected ? { backgroundColor: channel.color } : {}}
                      >
                        <Icon className="w-4 h-4" />
                        {channel.name}
                        {isSelected && <Check className="w-4 h-4" />}
                      </Button>
                    );
                  })}
                </div>
                
                <div className="flex items-center gap-2 mt-4">
                  <Switch
                    checked={fallbackEnabled}
                    onCheckedChange={setFallbackEnabled}
                  />
                  <span className="text-sm">Enable fallback (try next channel if delivery fails)</span>
                </div>
              </div>
              
              {/* Journey Steps (if journey type) */}
              {campaignType === 'journey' ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium">Journey Steps</h3>
                    <Button variant="outline" size="sm" onClick={addJourneyStep} className="gap-2">
                      <Plus className="w-4 h-4" />
                      Add Step
                    </Button>
                  </div>
                  <div className="space-y-6">
                    {journeySteps.map((step, idx) => (
                      <JourneyStep
                        key={idx}
                        step={step}
                        index={idx}
                        onUpdate={(updated) => {
                          const newSteps = [...journeySteps];
                          newSteps[idx] = updated;
                          setJourneySteps(newSteps);
                        }}
                        onRemove={() => {
                          if (journeySteps.length > 1) {
                            setJourneySteps(journeySteps.filter((_, i) => i !== idx));
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                // Content for other campaign types
                <div className="space-y-4">
                  {/* SMS Content */}
                  {selectedChannels.includes('sms') && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium">SMS Content</label>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={generateContent}
                          disabled={generating}
                          className="gap-2"
                        >
                          {generating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4" />
                          )}
                          AI Generate
                        </Button>
                      </div>
                      <Textarea
                        placeholder="Enter SMS content... Use {name}, {points} for personalization"
                        value={smsContent}
                        onChange={(e) => setSmsContent(e.target.value)}
                        className="min-h-[100px]"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {smsContent.length}/160 characters ({Math.ceil(smsContent.length / 160)} SMS)
                      </p>
                    </div>
                  )}
                  
                  {/* WhatsApp Content */}
                  {selectedChannels.includes('whatsapp') && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">WhatsApp Template</label>
                      <Textarea
                        placeholder="Enter WhatsApp message template..."
                        value={whatsappContent}
                        onChange={(e) => setWhatsappContent(e.target.value)}
                        className="min-h-[100px]"
                      />
                    </div>
                  )}
                  
                  {/* Meta Ads Content */}
                  {selectedChannels.includes('meta_ads') && (
                    <Card className="bg-gradient-to-br from-pink-500/10 to-purple-500/5 border-pink-500/20">
                      <CardContent className="p-4">
                        <h3 className="font-medium mb-3 flex items-center gap-2">
                          <Instagram className="w-5 h-5 text-pink-500" />
                          Meta Ads Configuration
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          A Custom Audience will be created from your selected segment. You can also create a Lookalike audience.
                        </p>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">Custom Audience</Badge>
                          <Badge variant="outline">Lookalike (1%)</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Step 4: Review */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Review Campaign</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Summary */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Campaign Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name</span>
                      <span className="font-medium">{campaignName || 'Untitled'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Type</span>
                      <span className="font-medium">
                        {CAMPAIGN_TYPES.find(t => t.id === campaignType)?.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Segment</span>
                      <span className="font-medium">
                        {SEGMENTS.find(s => s.id === selectedSegment)?.name || 'Not selected'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Est. Audience</span>
                      <span className="font-medium">
                        {audiencePreview?.total?.toLocaleString() || '~'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Channels */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Delivery Channels</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedChannels.map((channelId, idx) => {
                        const channel = CHANNELS.find(c => c.id === channelId);
                        const Icon = channel?.icon || MessageSquare;
                        return (
                          <div key={channelId} className="flex items-center gap-2">
                            <Badge variant="outline" className="gap-1">
                              {idx + 1}
                            </Badge>
                            <Icon className="w-4 h-4" style={{ color: channel?.color }} />
                            <span>{channel?.name}</span>
                            {idx < selectedChannels.length - 1 && fallbackEnabled && (
                              <ArrowRight className="w-4 h-4 text-muted-foreground ml-auto" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {fallbackEnabled && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Fallback enabled: Will try next channel if delivery fails
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              {/* Content Preview */}
              {campaignType !== 'journey' && smsContent && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Content Preview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="p-4 rounded-lg bg-muted/50 font-mono text-sm">
                      {smsContent}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Journey Preview */}
              {campaignType === 'journey' && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Journey Steps ({journeySteps.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 overflow-x-auto pb-2">
                      {journeySteps.map((step, idx) => {
                        const channel = CHANNELS.find(c => c.id === step.channel);
                        return (
                          <div key={idx} className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-sm font-medium">
                              D+{step.day}
                            </div>
                            <div className="text-xs text-muted-foreground">{channel?.name}</div>
                            {idx < journeySteps.length - 1 && (
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-500 text-sm">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4 text-red-500 hover:text-red-400" />
          </button>
        </div>
      )}
      
      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
          disabled={currentStep === 0}
        >
          Previous
        </Button>
        
        <div className="flex items-center gap-2">
          {currentStep < 3 ? (
            <Button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={
                (currentStep === 0 && !campaignName) ||
                (currentStep === 1 && !selectedSegment) ||
                (currentStep === 2 && selectedChannels.length === 0)
              }
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <>
              <Button 
                variant="outline" 
                onClick={() => saveCampaign(false)}
                disabled={saving || executing}
              >
                {saving && !executing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save as Draft'
                )}
              </Button>
              <Button
                onClick={() => saveCampaign(true)}
                disabled={saving || executing}
                className="gap-2 bg-gradient-to-r from-green-500 to-emerald-600"
                data-testid="save-execute-btn"
              >
                {saving || executing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                {executing ? 'Executing...' : saving ? 'Saving...' : 'Save & Execute'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
